"use client"

import * as React from 'react'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import { createCrud } from '@open-mercato/ui/backend/utils/crud'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@open-mercato/ui/primitives/dialog'
import { Button } from '@open-mercato/ui/primitives/button'
import { Input } from '@open-mercato/ui/primitives/input'
import { DateTimePicker } from '@open-mercato/ui/backend/inputs/DateTimePicker'
import { Label } from '@open-mercato/ui/primitives/label'
import type { LegListItem } from './legTable'

type LegFormDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  leg?: LegListItem | null
  onSuccess: () => void
}

const VEHICLE_TYPES = ['TRUCK', 'SOLO', 'TRAILER', 'MEGA', 'TANKER', 'REEFER'] as const

function toDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return ''
  // datetime-local needs "YYYY-MM-DDTHH:MM"
  return iso.slice(0, 16)
}

export function LegFormDialog({ open, onOpenChange, leg, onSuccess }: LegFormDialogProps) {
  const t = useT()
  const isEdit = Boolean(leg)
  const { runMutation } = useGuardedMutation({
    contextId: leg ? `leg-edit-${leg.id}` : 'leg-create',
  })

  const [requiredVehicleType, setRequiredVehicleType] = React.useState<string>('')
  const [scheduledPickupAt, setScheduledPickupAt] = React.useState<Date | null>(null)
  const [scheduledDeliveryAt, setScheduledDeliveryAt] = React.useState<Date | null>(null)
  const [notes, setNotes] = React.useState('')
  const [cargoId, setCargoId] = React.useState('')
  const [pending, setPending] = React.useState(false)

  React.useEffect(() => {
    if (open) {
      setRequiredVehicleType(leg?.requiredVehicleType ?? '')
      setScheduledPickupAt(leg?.scheduledPickupAt ? new Date(leg.scheduledPickupAt) : null)
      setScheduledDeliveryAt(leg?.scheduledDeliveryAt ? new Date(leg.scheduledDeliveryAt) : null)
      setNotes(leg?.notes ?? '')
      setCargoId(leg?.cargoId ?? '')
    } else {
      setRequiredVehicleType('')
      setScheduledPickupAt(null)
      setScheduledDeliveryAt(null)
      setNotes('')
      setCargoId('')
    }
  }, [open, leg])

  const handleSubmit = React.useCallback(async () => {
    setPending(true)
    try {
      const payload: Record<string, unknown> = {}
      if (requiredVehicleType) payload.requiredVehicleType = requiredVehicleType
      if (scheduledPickupAt) payload.scheduledPickupAt = scheduledPickupAt.toISOString()
      if (scheduledDeliveryAt) payload.scheduledDeliveryAt = scheduledDeliveryAt.toISOString()
      if (notes) payload.notes = notes
      if (cargoId) payload.cargoId = cargoId

      if (isEdit && leg) {
        await runMutation({
          context: { kind: 'road.leg', record: { id: leg.id } },
          mutationPayload: payload,
          operation: async () => {
            const res = await apiCall(`/api/road/legs/${leg.id}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            })
            if (!res.ok) {
              throw new Error(t('dispatch.messages.updateError', 'Failed to update leg.'))
            }
          },
        })
        flash(t('dispatch.messages.updateSuccess', 'Leg updated.'), 'success')
      } else {
        await runMutation({
          context: { kind: 'road.leg', record: null },
          mutationPayload: payload,
          operation: async () => {
            await createCrud('road/legs', payload, {
              errorMessage: t('dispatch.messages.createError', 'Failed to create leg.'),
            })
          },
        })
        flash(t('dispatch.messages.createSuccess', 'Leg created.'), 'success')
      }
      onOpenChange(false)
      onSuccess()
    } catch {
      flash(
        isEdit
          ? t('dispatch.messages.updateError', 'Failed to update leg.')
          : t('dispatch.messages.createError', 'Failed to create leg.'),
        'error',
      )
    } finally {
      setPending(false)
    }
  }, [isEdit, leg, requiredVehicleType, scheduledPickupAt, scheduledDeliveryAt, notes, cargoId, runMutation, t, onOpenChange, onSuccess])

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') void handleSubmit()
    if (e.key === 'Escape') onOpenChange(false)
  }, [handleSubmit, onOpenChange])

  const title = isEdit
    ? t('dispatch.leg.form.title.edit', 'Edit leg')
    : t('dispatch.leg.form.title.create', 'Create leg')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onKeyDown={handleKeyDown} className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-1.5">
            <Label htmlFor="requiredVehicleType">{t('dispatch.table.filters.vehicleType', 'Vehicle type')}</Label>
            <select
              id="requiredVehicleType"
              className="h-9 w-full rounded border px-2 text-sm"
              value={requiredVehicleType}
              onChange={(e) => setRequiredVehicleType(e.target.value)}
              disabled={pending}
            >
              <option value="">—</option>
              {VEHICLE_TYPES.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="scheduledPickupAt">{t('dispatch.table.columns.scheduledPickupAt', 'Scheduled pickup')}</Label>
              <DateTimePicker
                value={scheduledPickupAt}
                onChange={setScheduledPickupAt}
                disabled={pending}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="scheduledDeliveryAt">{t('dispatch.table.columns.scheduledDeliveryAt', 'Scheduled delivery')}</Label>
              <DateTimePicker
                value={scheduledDeliveryAt}
                onChange={setScheduledDeliveryAt}
                disabled={pending}
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="legNotes">{t('dispatch.leg.form.fields.notes', 'Notes')}</Label>
            <Input
              id="legNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder=""
              disabled={pending}
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="cargoId">{t('dispatch.table.columns.cargoId', 'Cargo ID')}</Label>
            <Input
              id="cargoId"
              value={cargoId}
              onChange={(e) => setCargoId(e.target.value)}
              placeholder=""
              disabled={pending}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            {t('dispatch.actions.cancelEdit', 'Cancel')}
          </Button>
          <Button type="button" onClick={() => { void handleSubmit() }} disabled={pending}>
            {pending
              ? t('dispatch.actions.saving', 'Saving…')
              : isEdit
                ? t('dispatch.actions.save', 'Save')
                : t('dispatch.leg.form.title.create', 'Create leg')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
